import { registerSW } from 'virtual:pwa-register';
import { getEnvironmentIndicator } from '@/lib/environment-indicator';

const manifestLinkSelectorAny = 'link[rel="manifest"]';
const baseAppTitle = 'Nava Backoffice';
const baseAppleAppTitle = 'Backoffice';
let runtimeManifestUrl: string | null = null;

function ensureManifestLink(doc: Document = document): void {
  const href = getRuntimeManifestHref();
  const existingLink = doc.querySelector<HTMLLinkElement>(manifestLinkSelectorAny);

  if (existingLink?.href === href || existingLink?.getAttribute('href') === href) {
    return;
  }

  const link = existingLink ?? doc.createElement('link');
  link.rel = 'manifest';
  link.href = href;

  if (!existingLink) {
    doc.head.appendChild(link);
  }
}

function applyEnvironmentTitle(doc: Document = document): void {
  const suffix = getEnvironmentTitleSuffix();

  doc.title = `${baseAppTitle}${suffix}`;

  const appleTitle = doc.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
  if (appleTitle) {
    appleTitle.content = `${baseAppleAppTitle}${suffix}`;
  }
}

function getEnvironmentTitleSuffix(): '' | ' [LOCAL]' | ' [DEV]' {
  const indicator = getEnvironmentIndicator();
  return indicator ? ` [${indicator.label}]` : '';
}

function getRuntimeManifestHref(): string {
  const suffix = getEnvironmentTitleSuffix();
  revokeRuntimeManifestUrl();
  const manifestUrl = (path: string) => new URL(path, window.location.origin).toString();
  const manifest = {
    id: manifestUrl('/'),
    name: `${baseAppTitle}${suffix}`,
    short_name: `${baseAppleAppTitle}${suffix}`,
    description: 'Operational tools for managing Nava studies and users',
    start_url: manifestUrl('/dashboard'),
    scope: manifestUrl('/'),
    display: 'standalone',
    background_color: '#FAFAF9',
    theme_color: '#dd7d7d',
    icons: [
      {
        src: manifestUrl('/pwa-64x64.png'),
        sizes: '64x64',
        type: 'image/png',
      },
      {
        src: manifestUrl('/pwa-192x192.png'),
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: manifestUrl('/pwa-512x512.png'),
        sizes: '512x512',
        type: 'image/png',
      },
      {
        src: manifestUrl('/maskable-icon-512x512.png'),
        sizes: '512x512',
        type: 'image/png',
        purpose: 'maskable',
      },
    ],
  };
  runtimeManifestUrl = URL.createObjectURL(
    new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
  );

  return runtimeManifestUrl;
}

function revokeRuntimeManifestUrl(): void {
  if (!runtimeManifestUrl) return;

  URL.revokeObjectURL(runtimeManifestUrl);
  runtimeManifestUrl = null;
}

export function configurePwaSupport(): void {
  applyEnvironmentTitle();
  ensureManifestLink();
  if (import.meta.env.DEV && 'serviceWorker' in navigator) {
    void navigator.serviceWorker.register('/backoffice-push-sw.js', { scope: '/' });
  }
  registerSW({ immediate: true });
}
